import { defineStore } from 'pinia'

import { createCompressedFieldsSerializer } from '@/lib/persistedStateSerializer'

// `catalogContent` is the Contentful catalog payload, ~3 MB uncompressed. It lives in its own store
// because `pinia-plugin-persistedstate` re-serializes a whole store on every mutation: while it sat
// in the `catalog` store, dismissing a notification or an upsell recompressed the payload on the
// main thread. Here it is only written when the content itself changes.
//
// Rationale: `docs/features/2026.09.16-cart-configure-null-selections/decisions/2026.09.16-001-catalog-content-persisted-compressed.md`

interface CatalogContentStoreState {
  catalogContent: any
}

const serializer = createCompressedFieldsSerializer(['catalogContent'])

// Entries written before this store existed hold the content compressed inside the `catalog` entry.
// Read it once so an existing customer keeps their content across the release; the `catalog` store
// omits the field on hydrate and drops it from storage on its next write. Removable once released
// entries have rolled over.
function readLegacyCatalogContent(): unknown {
  try {
    const raw = localStorage.getItem('catalog')
    if (!raw) return null

    return serializer.deserialize(raw).catalogContent ?? null
  } catch {
    return null
  }
}

export const useCatalogContentStore = defineStore('catalogContent', {
  state: (): CatalogContentStoreState => ({
    // All state properties should be added to the manual resetStore action method below
    catalogContent: null
  }),
  actions: {
    resetStore(): void {
      // This method is used to reset the store to its initial state manually because of the use of persist
      // Any state property added above should be reset here
      this.catalogContent = null
    }
  },
  persist: {
    serializer,
    afterHydrate(ctx) {
      if (ctx.store.catalogContent !== null) return

      const legacy = readLegacyCatalogContent()
      if (!legacy) return

      ctx.store.catalogContent = legacy
      // The plugin subscribes for persistence after this hook, so write the entry explicitly or
      // the migrated content lives for one page load only.
      ctx.store.$persist()
    }
  }
})
